import { useEffect, useCallback, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';

// Keyboard shortcuts configuration
export const KEYBOARD_SHORTCUTS = {
  // Navigation
  'g d': { action: 'navigate', path: '/dashboard', description: 'Go to Dashboard', category: 'Navigation' },
  'g s': { action: 'navigate', path: '/subjects', description: 'Go to Subjects', category: 'Navigation' },
  'g l': { action: 'navigate', path: '/lectures', description: 'Go to Lectures', category: 'Navigation' },
  'g b': { action: 'navigate', path: '/study-buddy', description: 'Go to Study Buddy', category: 'Navigation' },
  'g g': { action: 'navigate', path: '/study-groups', description: 'Go to Study Groups', category: 'Navigation' },
  'g a': { action: 'navigate', path: '/achievements', description: 'Go to Achievements', category: 'Navigation' },
  'g p': { action: 'navigate', path: '/settings', description: 'Go to Settings', category: 'Navigation' },
  
  // Actions
  'n l': { action: 'create', type: 'lecture', description: 'New Lecture', category: 'Actions' },
  'n s': { action: 'create', type: 'subject', description: 'New Subject', category: 'Actions' },
  'n g': { action: 'create', type: 'group', description: 'New Study Group', category: 'Actions' },
  
  // UI Controls
  '/': { action: 'focus', target: 'search', description: 'Focus Search', category: 'UI' },
  'Escape': { action: 'close', description: 'Close Modal/Menu', category: 'UI' },
  '?': { action: 'help', description: 'Show Keyboard Shortcuts', category: 'UI' },
  
  // Study Buddy
  'c': { action: 'chat', description: 'Focus Chat Input', category: 'Study Buddy', context: '/study-buddy' },
  'v': { action: 'voice', description: 'Toggle Voice', category: 'Study Buddy', context: '/study-buddy' },
  
  // Quick Actions
  'r': { action: 'record', description: 'Start Recording', category: 'Quick Actions', context: '/lectures' },
};

// Hook for keyboard shortcuts
export function useKeyboardShortcuts(customHandlers = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [buffer, setBuffer] = useState('');
  const [showHelp, setShowHelp] = useState(false);
  const [lastKeyTime, setLastKeyTime] = useState(0);

  const handleAction = useCallback((shortcut, config) => {
    // Check if shortcut requires specific context
    if (config.context && !location.pathname.startsWith(config.context)) {
      return false;
    }

    switch (config.action) {
      case 'navigate':
        navigate(config.path);
        toast.success(`Navigated to ${config.description.replace('Go to ', '')}`);
        return true;
        
      case 'create':
        if (customHandlers.onCreate) {
          customHandlers.onCreate(config.type);
        }
        return true;
        
      case 'focus':
        const searchInput = document.querySelector('[data-testid="search-input"]');
        if (searchInput) {
          searchInput.focus();
          return true;
        }
        break;
        
      case 'close':
        // Dispatch escape event for modals
        document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }));
        return true;
        
      case 'help':
        setShowHelp(prev => !prev);
        return true;
        
      case 'chat':
        const chatInput = document.querySelector('[data-testid="chat-input"]');
        if (chatInput) {
          chatInput.focus();
          return true;
        }
        break;
        
      case 'voice':
        const voiceBtn = document.querySelector('[data-testid="voice-toggle"]');
        if (voiceBtn) {
          voiceBtn.click();
          return true;
        }
        break;
        
      case 'record':
        const recordBtn = document.querySelector('[data-testid="record-button"]');
        if (recordBtn) {
          recordBtn.click();
          return true;
        }
        break;
        
      default:
        if (customHandlers[config.action]) {
          customHandlers[config.action](config);
          return true;
        }
    }
    return false;
  }, [navigate, location, customHandlers]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore if user is typing in an input/textarea
      const target = e.target;
      const isTyping = target.tagName === 'INPUT' || 
                       target.tagName === 'TEXTAREA' || 
                       target.isContentEditable;
      
      // Allow certain shortcuts even when typing
      if (isTyping && e.key !== 'Escape' && e.key !== '?') {
        return;
      }

      // Don't trigger shortcuts with modifier keys (except for specific cases)
      if (e.ctrlKey || e.metaKey || e.altKey) {
        return;
      }

      const now = Date.now();
      const key = e.key;
      
      // Reset buffer if too much time has passed
      const newBuffer = now - lastKeyTime > 1000 ? key : buffer + ' ' + key;
      
      // Check for single key shortcuts first
      if (KEYBOARD_SHORTCUTS[key]) {
        e.preventDefault();
        if (handleAction(key, KEYBOARD_SHORTCUTS[key])) {
          setBuffer('');
          return;
        }
      }
      
      // Check for multi-key shortcuts
      const trimmedBuffer = newBuffer.trim();
      if (KEYBOARD_SHORTCUTS[trimmedBuffer]) {
        e.preventDefault();
        handleAction(trimmedBuffer, KEYBOARD_SHORTCUTS[trimmedBuffer]);
        setBuffer('');
        setLastKeyTime(0);
        return;
      }
      
      // Check if buffer could match any shortcut
      const possibleMatch = Object.keys(KEYBOARD_SHORTCUTS).some(
        shortcut => shortcut.startsWith(trimmedBuffer)
      );
      
      if (possibleMatch) {
        setBuffer(trimmedBuffer);
        setLastKeyTime(now);
      } else {
        setBuffer('');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [buffer, lastKeyTime, handleAction]);

  return { showHelp, setShowHelp, currentBuffer: buffer };
}

// Keyboard shortcuts help modal component
export function KeyboardShortcutsHelp({ isOpen, onClose }) {
  if (!isOpen) return null;

  const categories = {};
  Object.entries(KEYBOARD_SHORTCUTS).forEach(([shortcut, config]) => {
    if (!categories[config.category]) {
      categories[config.category] = [];
    }
    categories[config.category].push({ shortcut, ...config });
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative bg-slate-900  shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden border border-[var(--isp-glass-border)]">
        {/* Header */}
        <div className="px-6 py-4 border-b border-[var(--isp-glass-border)] flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">Keyboard Shortcuts</h2>
            <p className="text-sm text-[var(--isp-body)]">Press ? to toggle this menu</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-[var(--isp-glass-inner)] rounded-lg transition-colors"
          >
            <svg className="w-5 h-5 text-[var(--isp-body)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <div className="grid md:grid-cols-2 gap-6">
            {Object.entries(categories).map(([category, shortcuts]) => (
              <div key={category}>
                <h3 className="text-sm font-semibold text-purple-400 mb-3">{category}</h3>
                <div className="space-y-2">
                  {shortcuts.map(({ shortcut, description }) => (
                    <div key={shortcut} className="flex items-center justify-between py-1">
                      <span className="text-sm text-[var(--isp-body)]">{description}</span>
                      <kbd className="px-2 py-1 bg-slate-800 rounded text-xs font-mono text-[var(--isp-heading)] border border-[var(--isp-glass-border)]">
                        {shortcut}
                      </kbd>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Footer */}
        <div className="px-6 py-3 border-t border-[var(--isp-glass-border)] bg-[var(--isp-glass)] backdrop-blur-xl">
          <p className="text-xs text-[var(--isp-body)] text-center">
            Pro tip: Multi-key shortcuts like <kbd className="px-1 bg-slate-700 rounded">g d</kbd> must be pressed within 1 second
          </p>
        </div>
      </div>
    </div>
  );
}

export default useKeyboardShortcuts;
